/**
 * `useFormatters` - the single date/currency/number control surface.
 *
 * One hook, shared by the admin app, the consumer app, and every shared
 * component. It reads the active organisation's locale + currency from
 * `FormattingProvider` (fed by org preferences in each app) and returns
 * formatting functions already bound to them, so callers never pass a locale
 * and every surface formats identically to the rest of the platform.
 *
 *   const { formatDate, formatDateTime, formatCurrency } = useFormatters()
 *   formatDate(holding.created_at)        // '15 Jan 2025' (en-GB) / 'Jan 15, 2025' (en-US)
 *   formatDateTime(entry.occurred_at)     // date + time, org locale
 *   formatCurrency(1234.5)                // '£1,234.50' (org currency)
 *
 * `useFormatting` (in `@/admin/hooks/use-formatting`) is an alias of this hook
 * kept for the admin import path. Prefer `useFormatters` in new code.
 */

import { useMemo } from 'react'
import { useFormattingContext } from './use-formatting-context'
import {
  formatCurrency as fmtCurrency,
  formatCurrencyCompact as fmtCurrencyCompact,
  formatCurrencyValue as fmtCurrencyValue,
  formatDate as fmtDate,
  formatDateNumeric as fmtDateNumeric,
  formatDateLong as fmtDateLong,
  formatDateTime as fmtDateTime,
  formatNumber as fmtNumber,
  formatTime as fmtTime,
  getCurrencySymbol as getCurrSym,
} from './format'

export interface Formatters {
  /** BCP 47 locale tag currently in effect (e.g. 'en-GB'). */
  locale: string
  /** ISO 4217 currency code currently in effect (e.g. 'GBP'). */
  currency: string
  /** Localised currency symbol for the active currency (e.g. '£'). */
  currencySymbol: string
  /** Short date, e.g. '15 Jan 2025' / 'Jan 15, 2025'. */
  formatDate: (iso: string) => string
  /** All-numeric date, e.g. '15/01/2025' / '01/15/2025'. */
  formatDateNumeric: (iso: string) => string
  /** Long date, e.g. '15 January 2025' / 'January 15, 2025'. */
  formatDateLong: (iso: string) => string
  /** Date + time, e.g. '15 Jan 2025, 14:30'. */
  formatDateTime: (iso: string) => string
  /** Time only, e.g. '14:30'. */
  formatTime: (iso: string) => string
  /** Currency from a numeric amount, e.g. '£1,234.50'. */
  formatCurrency: (amount: number) => string
  /** Compact currency for dashboards, e.g. '£1.2M'. */
  formatCurrencyCompact: (amount: number) => string
  /** Currency from a raw value or `{ amount, currency }` money object. */
  formatCurrencyValue: (value: unknown) => string
  /** Plain number with locale grouping, e.g. '12,345'. */
  formatNumber: (n: number) => string
}

export function useFormatters(): Formatters {
  const { locale, currency } = useFormattingContext()
  return useMemo(
    () => ({
      locale,
      currency,
      currencySymbol: getCurrSym(currency, locale),
      formatDate: (iso: string) => fmtDate(iso, locale),
      formatDateNumeric: (iso: string) => fmtDateNumeric(iso, locale),
      formatDateLong: (iso: string) => fmtDateLong(iso, locale),
      formatDateTime: (iso: string) => fmtDateTime(iso, locale),
      formatTime: (iso: string) => fmtTime(iso, locale),
      formatCurrency: (amount: number) => fmtCurrency(amount, currency, locale),
      formatCurrencyCompact: (amount: number) => fmtCurrencyCompact(amount, currency, locale),
      formatCurrencyValue: (value: unknown) => fmtCurrencyValue(value, currency, locale),
      formatNumber: (n: number) => fmtNumber(n, locale),
    }),
    [locale, currency],
  )
}
